import { initFlowbite } from 'flowbite';
import Croppie from 'croppie';
import TomSelect from 'tom-select';


/* ========================================================================
   NASTAVENÍ DEBUG MODU
   True = Detailní výpisy do konzole (pro vývoj).
   False = Ticho (pro produkci).
======================================================================== */
const DEBUG = true;

/**
 * Centrální logovací funkce.
 * Používáme ji místo console.log, abychom mohli výpisy globálně vypnout.
 */
const log = (msg, data = null) => {
    if (DEBUG) {
        if (data) console.log(`🔧 [UI-UTILS]: ${msg}`, data);
        else console.log(`🔧 [UI-UTILS]: ${msg}`);
    }
};

/* ========================================================================
   OVERLAY HELPERS
   Funkce pro ovládání overlaye z JavaScriptu (např. z Naja).
======================================================================== */
function getOverlay() { return document.getElementById('ajax-overlay'); }

export const showOverlay = (reason = '') => {
    log(`Zobrazuji overlay. Důvod: ${reason}`);
    getOverlay()?.classList.add('visible');
};

export const hideOverlay = () => {
    log('Skrývám overlay.');
    getOverlay()?.classList.remove('visible');
};

/* ========================================================================
   FLOWBITE WRAPPER
   Obaleno do try-catch, aby chyba v knihovně nezhroutila celý web.
======================================================================== */
export function initFlowbiteComponents() {
    try {
        if (typeof initFlowbite === 'function') {
            initFlowbite();
            log('Flowbite inicializováno.');
        }
    } catch (e) {
        console.error('❌ Flowbite chyba:', e);
    }
}

/* ========================================================================
   CROPPIE LOGIKA (Ořezávání obrázků)
   Tato funkce je "chytrá" - pokud nenajde prvky, okamžitě skončí.
======================================================================== */
// app.js – nahraď stávající setupCroppieFlow touto verzí

let croppieInstance = null

export function destroyCroppie() {
    if (croppieInstance) {
        croppieInstance.destroy()
        croppieInstance = null
    }
}
export function setupCroppieFlow(root = document) {
    const el = {
        container:   root.querySelector('#croppie-container'),
        interaction: root.querySelector('#croppie-interaction'),
        croppie:     root.querySelector('[data-croppie]'),
        input:       root.querySelector('input[type="file"][name="image"]'),
        form:        root.querySelector('input[type="file"][name="image"]')?.closest('form'),
        origAvatar:  root.querySelector('#avatar-container'),
        inputAvatar: root.querySelector('#input-file-avatar'),
        inputAvatarBtn: root.querySelector('#input-file-avatar-btn'),
        saveBtn:     root.querySelector('#saveCrop'),
        cancelBtn:   root.querySelector('#cancel-crop'),
    }

    if (!el.container || !el.croppie || !el.input || !el.form) return
    if (el.input.dataset.croppieReady) return
    el.input.dataset.croppieReady = 'true'

    log('✅ Croppie prvky nalezeny! Inicializuji logiku.')

    el.input.addEventListener('change', e => {
        const file = e.target.files[0]
        if (!file) return
        log(`📂 Vybrán soubor: ${file.name}`)
        const reader = new FileReader()
        reader.onload = ev => {
            if (el.origAvatar) el.origAvatar.style.display = 'none'
            if (el.inputAvatar) el.inputAvatar.style.display = 'none'
            if (el.inputAvatarBtn) el.inputAvatarBtn.style.display = 'none'
            el.container.style.display = 'block'
            el.interaction.style.display = 'flex'
            if (croppieInstance) croppieInstance.destroy()
            croppieInstance = new Croppie(el.croppie, {
                viewport:   { width: 220, height: 220, type: 'square' },
                boundary:   { width: 300, height: 300 },
                enableExif: true,
                showZoomer: true,
            })
            croppieInstance.bind({ url: ev.target.result })
            log('✨ Croppie editor spuštěn.')
        }
        reader.readAsDataURL(file)
    })

    el.saveBtn?.addEventListener('click', async e => {
        if (!croppieInstance) return
        e.preventDefault()
        e.stopImmediatePropagation()
        log('💾 Ukládám ořez...')

        const base64 = await croppieInstance.result({
            type:    'base64',
            size:    'viewport',
            format:  'png',
            quality: 1,
        })

        log('🚀 Odesílám formulář.')
        const old = croppieInstance
        croppieInstance = null

        if (window.naja) {
            const formData = new FormData(el.form)
            formData.set('croppedImage', base64)

            // Signal: frm-personalForm → personalForm-send
            const formName = el.form.id.replace(/^frm-/, '')
            const signal = `${formName}-send`
            const url = `${window.location.pathname}?do=${signal}`

            log(`📡 Signal: ${signal}, URL: ${url}`)

            window.naja.makeRequest('POST', url, formData, {
                history: false,  // nezapisuj do history API – po refreshi nevznikne 404
                fetch: { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
            })
        } else {
            el.form.method = 'post'
            let hidden = el.form.querySelector('input[name="croppedImage"]')
            if (!hidden) {
                hidden = document.createElement('input')
                hidden.type = 'hidden'
                hidden.name = 'croppedImage'
                el.form.appendChild(hidden)
            }
            hidden.value = base64
            el.form.submit()
        }

        croppieInstance = old
    })

    el.cancelBtn?.addEventListener('click', () => {
        log('❌ Ořez zrušen.')
        destroyCroppie()
        el.container.style.display = 'none'
        el.interaction.style.display = 'none'
        if (el.origAvatar) el.origAvatar.style.display = 'block'
        if (el.inputAvatar) el.inputAvatar.style.display = 'block'
        if (el.inputAvatarBtn) el.inputAvatarBtn.style.display = 'block'
        el.input.value = ''
    })
}

/* ========================================================================
   POKROČILÁ OVERLAY LOGIKA
   Doplňuje inline JS o chytrost (odesílání tlačítek ve formuláři).
======================================================================== */
export function setupGlobalOverlayLogic() {
    log('Aktivuji pokročilou logiku pro Overlay (form submits).');

    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-overlay]');

        // Pokud to není overlay prvek nebo je to AJAX (ten řeší Naja), ignorujeme
        if (!trigger || trigger.classList.contains('ajax')) return;

        // Řešíme pouze tlačítka typu Submit
        if (trigger.tagName === 'BUTTON') {
            if (trigger.type === 'button') return; // Ignorovat type="button"

            // Ignorovat UI interakce (aby overlay neblikal při přepínání tabů)
            if (trigger.matches('[data-tabs-target], [data-modal-target], [data-toggle]')) {
                hideOverlay();
                return;
            }

            const form = trigger.closest('form');
            if (form && (!trigger.type || trigger.type === 'submit')) {
                // Validace formuláře (required pole atd.)
                if (!form.checkValidity()) {
                    log('⚠️ Formulář není validní -> Overlay ruším.');
                    hideOverlay();
                    return;
                }

                showOverlay('Formulář Submit');
                event.preventDefault();

                // Malé zpoždění pro vykreslení overlaye
                setTimeout(() => {
                    // Pokud tlačítko má 'name', musíme ho poslat jako hidden input
                    // Jinak by Nette nevědělo, které tlačítko bylo stisknuto
                    if (trigger.name && !form.querySelector(`input[name="${trigger.name}"]`)) {
                        log(`➕ Přidávám hidden input: ${trigger.name}`);
                        const i = document.createElement('input');
                        i.type = 'hidden';
                        i.name = trigger.name;
                        i.value = trigger.value || '';
                        form.appendChild(i);
                    }
                    form.submit();
                }, 50);
            }
        }
    });
}

/* ========================================================================
   LEGACY SNIPPETS
   Podpora pro starší kód (funkce definované globálně ve window).
======================================================================== */
export function initLegacySnippets(root = document) {
    const legacyFuncs = [
        'initSearchInput',
        'initTinyMCE',
        'initClipboardCopy',
        'initClipboardRead',
        'initBootstrapUI'
    ];

    legacyFuncs.forEach(fn => {
        try {
            if (typeof window[fn] === 'function') {
                // log(`Spouštím legacy funkci: ${fn}`);
                window[fn](root);
            }
        } catch (e) { console.error(`Chyba v legacy funkci ${fn}`, e); }
    });


}
export function initTomSelect(selectors = []) {
    const selectorList = Array.isArray(selectors) ? selectors : [selectors];
    let initializedCount = 0;

    for (const selector of selectorList) {
        const elements = document.querySelectorAll(selector);

        for (const element of elements) {
            if (!element) continue;
            if (element.tomselect) continue;

            new TomSelect(element, {
                create: true,
                maxItems: 1,
                closeAfterSelect: true,
                sortField: { field: "text", direction: "asc" },

                onItemAdd() {
                    this.blur();
                },
                onDropdownClose() {
                    this.blur();
                },
            });

            initializedCount++;
        }
    }

    return initializedCount > 0;
}

let dayScrollNavCleanup = null;

export function initStickyDayNav(root = document) {
    const navLinks = Array.from(root.querySelectorAll('[data-day-nav]'));
    const sections = Array.from(root.querySelectorAll('[data-day-section]'));

    // Když na stránce sticky navigace dnů není, nic nedělej
    if (!navLinks.length || !sections.length) {
        if (typeof dayScrollNavCleanup === 'function') {
            dayScrollNavCleanup();
            dayScrollNavCleanup = null;
        }
        return false;
    }

    // Při reinitu po AJAXu odpoj staré listenery
    if (typeof dayScrollNavCleanup === 'function') {
        dayScrollNavCleanup();
        dayScrollNavCleanup = null;
    }

    const activeClasses = ['bg-[#C8522A]', 'text-white'];
    const inactiveClasses = ['bg-gray-100', 'text-gray-700', 'dark:bg-gray-800', 'dark:text-gray-200'];

    const setActiveLink = (id, scrollNav = false) => {
        navLinks.forEach(link => {
            const isActive = link.dataset.dayNav === id;

            activeClasses.forEach(cls => link.classList.toggle(cls, isActive));
            inactiveClasses.forEach(cls => link.classList.toggle(cls, !isActive));
        });

        if (scrollNav) {
            const activeLink = navLinks.find(link => link.dataset.dayNav === id);
            if (activeLink) {
                activeLink.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest'
                });
            }
        }
    };

    const getStickyOffset = () => {
        const stickyWrapper =
            document.querySelector('#day-scroll-nav')?.closest('.sticky') ||
            root.querySelector('#day-scroll-nav')?.closest('.sticky');

        const stickyHeight = stickyWrapper ? stickyWrapper.getBoundingClientRect().height : 0;

        // základní offset pro header + sticky nav
        return 80 + stickyHeight;
    };

    const findActiveSection = () => {
        const offset = getStickyOffset();
        let activeId = sections[0]?.dataset.daySection ?? null;
        let bestTop = -Infinity;

        sections.forEach(section => {
            const rect = section.getBoundingClientRect();
            const top = rect.top - offset;

            if (top <= 0 && top > bestTop) {
                bestTop = top;
                activeId = section.dataset.daySection;
            }
        });

        if (!activeId) {
            let nearestId = null;
            let nearestDistance = Infinity;

            sections.forEach(section => {
                const rect = section.getBoundingClientRect();
                const distance = Math.abs(rect.top - offset);

                if (distance < nearestDistance) {
                    nearestDistance = distance;
                    nearestId = section.dataset.daySection;
                }
            });

            activeId = nearestId;
        }

        return activeId;
    };

    let ticking = false;

    const updateActiveOnScroll = () => {
        if (ticking) return;

        ticking = true;

        requestAnimationFrame(() => {
            const activeId = findActiveSection();
            if (activeId) {
                setActiveLink(activeId, false);
            }
            ticking = false;
        });
    };

    const clickHandlers = [];

    navLinks.forEach(link => {
        const handler = (e) => {
            e.preventDefault();

            const id = link.dataset.dayNav;
            const target = document.getElementById(id);

            if (!target) return;

            setActiveLink(id, true);

            const offset = getStickyOffset();
            const targetTop = target.getBoundingClientRect().top + window.scrollY - offset;

            window.scrollTo({
                top: targetTop,
                behavior: 'smooth'
            });
        };

        link.addEventListener('click', handler);
        clickHandlers.push({ link, handler });
    });

    const onScroll = () => updateActiveOnScroll();
    const onResize = () => updateActiveOnScroll();
    const onOrientationChange = () => updateActiveOnScroll();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onOrientationChange);

    updateActiveOnScroll();
    setTimeout(updateActiveOnScroll, 120);
    setTimeout(updateActiveOnScroll, 400);

    dayScrollNavCleanup = () => {
        clickHandlers.forEach(({ link, handler }) => {
            link.removeEventListener('click', handler);
        });

        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onOrientationChange);
    };

    return true;
}